/* eslint-disable */
/**
 * Gift with Purchase (JS-only, multi-criteria)
 *
 * - Reads configuration from the global section JSON `<script data-gift-with-purchase-json>`
 * - Supports per-GWP criteria:
 *   - minimum_purchase: threshold in local currency (string number) -> cents
 *   - discount_code: reacts to a specific discount code applied in cart
 * - Adds/removes gifts programmatically via Ajax Cart API.
 * - Attaches line item properties:
 *    { _gift: "true", _gwp_id: "<stable-id>", "GWP Title": "<title>", "GWP Description": "<description>" }
 * - Renders a JS-only progress bar with multiple markers (thresholds) in the cart drawer.
 *
 * Ajax Cart API references:
 * - Add line item properties (incl. private underscore keys):
 *   https://shopify.dev/docs/api/ajax/reference/cart#add-line-item-properties
 * - Update discounts in the cart (we react to codes; optional future auto-apply):
 *   https://shopify.dev/docs/api/ajax/reference/cart#update-discounts-in-the-cart
 * - Cart JSON structure (discounts, cart_level_discount_applications, etc.):
 *   https://shopify.dev/docs/api/ajax/reference/cart#json-of-a-cart
 * - Bundled section rendering with cart endpoints:
 *   https://shopify.dev/docs/api/ajax/reference/cart#bundled-section-rendering
 */

(function () {
  'use strict';

  /**
   * @typedef {Object} GwpConfigItem
   * @property {string} id - Stable computed id for this GWP
   * @property {number} productId
   * @property {number} variantId
   * @property {string} title
   * @property {string} description
   * @property {"minimum_purchase"|"discount_code"} criteria
   * @property {number | undefined} minThresholdCents
   * @property {string | undefined} discountCodeLower
   * @property {string | undefined} endsAt - ISO date string for campaign end (metafield campaigns only)
   */

  /**
   * State container for the feature.
   */
  const state = {
    /** @type {GwpConfigItem[]} */
    gwps: [],
    /** @type {any|null} */
    lastCart: null,
    /** prevent concurrent syncs */
    syncing: false,
    /** track recent adds to detect manual removals */
    recentlyAddedAtMsByVariantId: new Map(),
    /** @type {string} Current country code */
    countryCode: '',
    /** @type {string} Shop base currency */
    shopCurrency: '',
    /** @type {string} Presentment currency */
    presentmentCurrency: '',
  };

  const SECTION_NAMES = [
    'cart-items',
    'cart-icon-bubble',
    'cart-live-region-text',
    'cart-footer',
  ];

  const SECTIONS_PARAM = SECTION_NAMES.join(',');
  const SECTIONS_URL = `${Shopify.routes.root}cart`;

  /**
   * ENTRY
   */
  whenDomReady(() => {
    try {
      const configData = parseGwpConfig();
      state.gwps = configData.gwps;
      state.countryCode = configData.countryCode;
      state.shopCurrency = configData.shopCurrency;
      state.presentmentCurrency = configData.presentmentCurrency;
    } catch (err) {
      // If config is malformed, disable gracefully
      return;
    }

    wireEvents();

    // Initial sync on load
    // Slightly defer to allow other cart initializers to finish
    setTimeout(() => {
      safeSync();
    }, 0);
  });

  /**
   * Parse config JSON emitted by the `sections/gift-with-purchase.liquid` section.
   * Expects a script tag: <script type="application/json" data-gift-with-purchase-json> ... </script>
   * Returns a normalized list of GWP items merged from section blocks and metafield campaigns.
   *
   * @returns {{gwps: GwpConfigItem[], countryCode: string, shopCurrency: string, presentmentCurrency: string}}
   */
  function parseGwpConfig() {
    const script = document.querySelector(
      'script[data-gift-with-purchase-json]'
    );
    if (!script) {
      return { gwps: [], countryCode: '', shopCurrency: '', presentmentCurrency: '' };
    }
    let parsed;
    try {
      parsed = JSON.parse(script.textContent || '{}');
    } catch (err) {
      throw new Error('Invalid JSON in data-gift-with-purchase-json');
    }

    // Extract context
    const countryCode = String(parsed?.countryCode || '').trim().toUpperCase();
    const shopCurrency = String(parsed?.shopCurrency || '').trim().toUpperCase();
    const presentmentCurrency = String(parsed?.presentmentCurrency || '').trim().toUpperCase();

    // Parse section block gifts
    const gifts = Array.isArray(parsed?.gifts) ? parsed.gifts : [];
    /** @type {GwpConfigItem[]} */
    const normalized = [];

    for (const raw of gifts) {
      const productObj = raw?.product || {};
      const title = String(raw?.title ?? '').trim();
      const description = String(raw?.description ?? '').trim();
      const criteria = String(raw?.criteria ?? '').trim();

      const productId = Number(productObj?.id || 0);
      const variantId =
        Number(productObj?.first_available_variant?.id) ||
        // fallback heuristics
        Number(
          (Array.isArray(productObj?.variants) &&
            productObj.variants.find((v) => v?.available)?.id) ||
          0
        ) ||
        Number(
          (Array.isArray(productObj?.variants) && productObj.variants[0]?.id) ||
          0
        );

      if (!productId || !variantId || !criteria) {
        // Skip misconfigured items silently
        continue;
      }

      /** Build a stable id derived from product, criteria and its key value */
      let keyPart = '';
      /** @type {number|undefined} */
      let minThresholdCents;
      /** @type {string|undefined} */
      let discountCodeLower;
      if (criteria === 'minimum_purchase') {
        const rawThreshold = String(
          raw?.minimum_purchase_threshold ?? ''
        ).trim();
        const numeric = Number(rawThreshold.replace(',', '.'));
        if (!Number.isFinite(numeric) || numeric <= 0) {
          // Skip invalid threshold
          continue;
        }
        minThresholdCents = Math.round(numeric * 100);
        keyPart = String(minThresholdCents);
      } else if (criteria === 'discount_code') {
        const code = String(raw?.discount_code ?? '').trim();
        if (!code) {
          continue;
        }
        discountCodeLower = code.toLowerCase();
        keyPart = discountCodeLower;
      } else {
        // Unknown criteria -> skip (extensible for future)
        continue;
      }

      const id = `${criteria}:${productId}:${variantId}:${keyPart}`;

      normalized.push({
        id,
        productId,
        variantId,
        title,
        description,
        criteria: /** @type any */ (criteria),
        minThresholdCents,
        discountCodeLower,
        endsAt: undefined,
      });
    }

    // Parse and merge metafield campaigns
    const metafieldCampaigns = parseMetafieldCampaigns(parsed);
    const allGwps = normalized.concat(metafieldCampaigns);

    return { gwps: allGwps, countryCode, shopCurrency, presentmentCurrency };
  }

  /**
   * Parse metafield campaigns from the JSON config.
   * Filters by country, date range, and returns normalized GWP items.
   * NOTE: minThresholdCents is kept in BASE CURRENCY cents (will convert cart subtotal for comparison)
   *
   * @param {any} parsed - The full parsed JSON config
   * @returns {GwpConfigItem[]}
   */
  function parseMetafieldCampaigns(parsed) {
    const campaigns = Array.isArray(parsed?.campaigns) ? parsed.campaigns : [];
    const countryCode = String(parsed?.countryCode || '').trim().toUpperCase();
    const now = new Date();

    console.log('[GWP] Parsing metafield campaigns:', {
      campaignCount: campaigns.length,
      countryCode,
      now: now.toISOString(),
    });

    /** @type {GwpConfigItem[]} */
    const normalized = [];

    for (const discount of campaigns) {
      const discountId = String(discount?.discountId || '').trim();
      const title = String(discount?.title || '').trim();
      const startsAt = discount?.startsAt;
      const endsAt = discount?.endsAt;

      console.log('[GWP] Processing discount:', {
        discountId,
        title,
        startsAt,
        endsAt,
      });

      if (!discountId) {
        console.log('[GWP] Skipping: no discountId');
        continue;
      }

      // Date filtering: skip if campaign is not active
      if (startsAt) {
        try {
          const startDate = new Date(startsAt);
          if (now < startDate) {
            console.log('[GWP] Skipping: campaign not started yet', {
              now: now.toISOString(),
              startsAt: startDate.toISOString(),
            });
            continue;
          }
        } catch (e) {
          console.log('[GWP] Skipping: invalid startsAt date format', e);
          continue;
        }
      }

      if (endsAt) {
        try {
          const endDate = new Date(endsAt);
          if (now > endDate) {
            console.log('[GWP] Skipping: campaign ended', {
              now: now.toISOString(),
              endsAt: endDate.toISOString(),
            });
            continue;
          }
        } catch (e) {
          console.log('[GWP] Skipping: invalid endsAt date format', e);
          continue;
        }
      }

      // Process each campaign config within this discount
      const campaignConfigs = Array.isArray(discount?.campaigns)
        ? discount.campaigns
        : [];

      console.log('[GWP] Processing campaign configs:', campaignConfigs.length);

      for (const campaign of campaignConfigs) {
        const targetCountryCodes = Array.isArray(campaign?.targetCountryCodes)
          ? campaign.targetCountryCodes.map((c) =>
            String(c).trim().toUpperCase()
          )
          : [];
        const gwpVariantIds = Array.isArray(campaign?.gwpVariantIds)
          ? campaign.gwpVariantIds
          : [];
        const minCartSubtotal = Number(campaign?.minCartSubtotal || 0);

        console.log('[GWP] Processing campaign config:', {
          targetCountryCodes,
          gwpVariantIds,
          minCartSubtotal,
        });

        // Country filtering: skip if current country not in target list
        if (
          countryCode &&
          targetCountryCodes.length > 0 &&
          !targetCountryCodes.includes(countryCode)
        ) {
          console.log('[GWP] Skipping: country not in target list', {
            countryCode,
            targetCountryCodes,
          });
          continue;
        }

        // Pick first available variant (first-available strategy)
        if (gwpVariantIds.length === 0) {
          console.log('[GWP] Skipping: no variant IDs');
          continue;
        }
        const variantIdStr = String(gwpVariantIds[0]).trim();
        const variantId = Number(
          variantIdStr.replace(/^gid:\/\/shopify\/ProductVariant\//, '')
        );
        console.log('[GWP] Extracted variant ID:', {
          variantIdStr,
          variantId,
        });

        if (!variantId || !Number.isFinite(variantId)) {
          console.log('[GWP] Skipping: invalid variant ID');
          continue;
        }

        // Keep threshold in BASE CURRENCY cents (no conversion needed here)
        // We'll convert the cart subtotal to base currency when comparing
        const minThresholdCents = Math.round(minCartSubtotal * 100);

        console.log('[GWP] Threshold (base currency cents):', {
          minCartSubtotal,
          minThresholdCents,
          note: 'Threshold is in BASE CURRENCY cents - cart will be converted for comparison',
        });

        // Build stable ID
        const id = `metafield:${discountId}:${variantId}`;

        const gwpItem = {
          id,
          productId: 0, // Not needed for metafield campaigns (variant ID is sufficient)
          variantId,
          title,
          description: '', // Metafield campaigns don't have per-campaign descriptions
          criteria: 'minimum_purchase',
          minThresholdCents,
          discountCodeLower: undefined,
          endsAt: endsAt || undefined,
        };

        console.log('[GWP] Adding metafield GWP:', gwpItem);
        normalized.push(gwpItem);
      }
    }

    console.log('[GWP] Total metafield GWPs parsed:', normalized.length);
    return normalized;
  }

  /**
   * Wire feature events
   */
  function wireEvents() {
    // Theme-level cart change events (if theme dispatches)
    document.addEventListener('theme:cart:change', () => {
      safeSync();
    });

    // When drawer opens, ensure progress UI exists/updates
    document.addEventListener('theme:cart:drawer:open', () => {
      safeSync();
    });

    // Tab focus or visibility changes can prompt updates (e.g., from other tabs)
    window.addEventListener('focus', () => {
      safeSync();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        safeSync();
      }
    });
    // Reposition markers on resize without refetching cart
    window.addEventListener('resize', () => {
      if (state.lastCart) renderProgress(state.lastCart);
    });
  }

  /**
   * Throttled sync guard
   */
  function safeSync() {
    if (state.syncing) return;
    state.syncing = true;
    performSync().finally(() => {
      state.syncing = false;
    });
  }

  /**
   * Core sync: fetch cart -> reconcile gifts -> render progress
   */
  async function performSync() {
    const prevCart = state.lastCart;
    const cart = await getCart();
    state.lastCart = cart;

    await reconcileGifts(cart, prevCart);
    renderProgress(cart);
  }

  /**
   * Fetch cart JSON
   * @returns {Promise<any>}
   */
  async function getCart() {
    const res = await fetch(`${Shopify.routes.root}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`GET /cart.js failed: ${res.status}`);
    return res.json();
  }

  /**
   * Add a gift by variant id with properties
   * @param {number} variantId
   * @param {Record<string,string>} properties
   */
  async function addGift(variantId, properties, options) {
    try {
      const silent = !!(options && options.silent);
      const payload = {
        items: [
          {
            id: variantId,
            quantity: 1,
            properties,
          },
        ],
        sections: SECTIONS_PARAM,
        sections_url: SECTIONS_URL,
      };
      const res = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        // OOS or other constraint -> ignore gracefully
        // 422 is common when inventory limits are hit
        await safeReadText(res);
      } else {
        const data = await safeReadJson(res);
        if (data && data.sections) {
          applyBundledSections(data.sections);
        }
        if (silent) {
          await refreshApiCartItemsSectionQuietly();
          preventDrawerOpenForMs(1500);
          dispatchDrawerUpdate();
        }
        state.recentlyAddedAtMsByVariantId.set(String(variantId), Date.now());
      }
    } catch (err) {
      /* no-op */
    } finally {
      const silent = !!(options && options.silent);
      if (!silent) {
        dispatchDrawerUpdate();
      }
    }
  }

  /**
   * Remove a gift by variant id
   * @param {number} variantId
   */
  async function removeGift(variantId, options) {
    try {
      const silent = !!(options && options.silent);
      const payload = {
        updates: {
          [variantId]: 0,
        },
        sections: SECTIONS_PARAM,
        sections_url: SECTIONS_URL,
      };
      const res = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        await safeReadText(res);
      } else {
        const data = await safeReadJson(res);
        if (data && data.sections) {
          applyBundledSections(data.sections);
        }
        if (silent) {
          await refreshApiCartItemsSectionQuietly();
          preventDrawerOpenForMs(1500);
          dispatchDrawerUpdate();
        }
      }
    } catch (err) {
      /* no-op */
    } finally {
      const silent = !!(options && options.silent);
      if (!silent) {
        dispatchDrawerUpdate();
      }
    }
  }

  function dispatchDrawerUpdate() {
    try {
      document.dispatchEvent(
        new CustomEvent('theme:cart:drawer:update', { detail: {} })
      );
    } catch {
      /* no-op */
    }
  }

  /**
   * Determine if a cart line is a GWP line (ours).
   * @param {any} item
   */
  function isGiftLine(item) {
    const props = item?.properties;
    if (!props || typeof props !== 'object') return false;
    const giftFlag = String(props['_gift'] ?? '').toLowerCase();
    const hasGwpId =
      typeof props['_gwp_id'] === 'string' && props['_gwp_id'].length > 0;
    return giftFlag === 'true' || hasGwpId;
  }

  /**
   * Compute subtotal excluding our gift lines.
   * Returns value in PRESENTMENT CURRENCY cents.
   * @param {any} cart
   */
  function computeSubtotalExcludingGifts(cart) {
    const total = Number(cart?.total_price || 0);
    const giftsTotal = (Array.isArray(cart?.items) ? cart.items : [])
      .filter(isGiftLine)
      .reduce((sum, it) => sum + Number(it?.line_price || 0), 0);
    const result = Math.max(0, total - giftsTotal);
    return result;
  }

  /**
   * Convert amount from presentment currency to base currency.
   * Uses Shopify.currency.rate if available, otherwise returns original amount.
   * @param {number} amountInPresentmentCents
   * @returns {number} Amount in base currency cents
   */
  function convertToBaseCurrency(amountInPresentmentCents) {
    try {
      // Shopify.currency.rate is the rate from base -> presentment
      // To convert presentment -> base, we divide by the rate
      const rate = Number(window?.Shopify?.currency?.rate || 1);
      const baseCents = Math.round(amountInPresentmentCents / rate);

      console.log('[GWP] Currency conversion (presentment -> base):', {
        presentmentCents: amountInPresentmentCents,
        rate,
        baseCents,
        shopCurrency: state.shopCurrency,
        presentmentCurrency: state.presentmentCurrency,
      });

      return baseCents;
    } catch (e) {
      console.warn('[GWP] Currency conversion failed, using presentment amount:', e);
      return amountInPresentmentCents;
    }
  }

  /**
   * Check if a GWP qualifies given the cart.
   * @param {GwpConfigItem} gwp
   * @param {any} cart
   * @param {number} subtotalExGiftsCents - in presentment currency
   */
  function qualifies(gwp, cart, subtotalExGiftsCents) {
    switch (gwp.criteria) {
      case 'minimum_purchase': {
        const need = gwp.minThresholdCents || 0;

        // Metafield GWPs have thresholds in base currency
        // Section block GWPs have thresholds in presentment currency
        let cartSubtotal = subtotalExGiftsCents;

        if (gwp.id.startsWith('metafield:')) {
          // Convert cart subtotal to base currency for comparison
          cartSubtotal = convertToBaseCurrency(subtotalExGiftsCents);
        }

        const ok = cartSubtotal >= need;
        return ok;
      }
      case 'discount_code': {
        const code = gwp.discountCodeLower || '';
        if (!code) return false;

        // Check cart-level discounts
        const apps = Array.isArray(cart?.cart_level_discount_applications)
          ? cart.cart_level_discount_applications
          : [];
        const cartLevelHit = apps.some(
          (d) =>
            String(d?.title || '').toLowerCase() === code ||
            String(d?.code || '').toLowerCase() === code
        );
        if (cartLevelHit) return true;

        // Check line-level discounts on items
        const items = Array.isArray(cart?.items) ? cart.items : [];
        for (const it of items) {
          const discounts = Array.isArray(it?.discounts) ? it.discounts : [];
          const itemHit = discounts.some(
            (d) =>
              String(d?.title || '').toLowerCase() === code ||
              String(d?.code || '').toLowerCase() === code
          );
          if (itemHit) return true;
        }
        return false;
      }
      // NOTE: Add new criteria branches here in the future.
      default:
        return false;
    }
  }

  /**
   * Find if a specific gift (by variantId and _gwp_id) is present in cart.
   * @param {any} cart
   * @param {GwpConfigItem} gwp
   */
  function findGiftLineInCart(cart, gwp) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const targetId = gwp.variantId;
    return items.find((it) => {
      const sameVariant =
        Number(it?.variant_id || it?.id || 0) === Number(targetId);
      if (!sameVariant) return false;
      // If our _gwp_id present and matches, consider ours first
      const props = it?.properties || {};
      const gwpId = String(props?._gwp_id || '');
      return (
        isGiftLine(it) &&
        (gwpId === gwp.id ||
          gwpId === '') /* legacy or missing id still counts as gift */
      );
    });
  }

  /**
   * Reconcile: add/remove gifts based on current cart and config.
   * Also respects "user removed" suppression in localStorage.
   * @param {any} cart
   */
  async function reconcileGifts(cart, prevCart) {
    console.log('[GWP] Starting reconciliation, active GWPs:', state.gwps.length);
    console.log('[GWP] Active GWP configs:', state.gwps);

    const now = new Date();
    const items = Array.isArray(cart?.items) ? cart.items : [];

    // Build a set of active metafield campaign IDs for quick lookup
    const activeMetafieldIds = new Set(
      state.gwps
        .filter((g) => g.id.startsWith('metafield:'))
        .map((g) => g.id)
    );

    console.log('[GWP] Active metafield IDs:', Array.from(activeMetafieldIds));

    // Expiry cleanup: remove expired or deactivated gift lines
    for (const item of items) {
      if (!isGiftLine(item)) continue;

      const props = item?.properties || {};
      const gwpId = String(props?._gwp_id || '');
      const endsAt = String(props?._gwp_ends_at || '').trim();
      const variantId = Number(item?.variant_id || item?.id || 0);

      let shouldRemove = false;

      // Check if gift has expired by date
      if (endsAt) {
        try {
          const endDate = new Date(endsAt);
          if (now > endDate) {
            shouldRemove = true;
          }
        } catch (e) {
          // Invalid date format, ignore
        }
      }

      // Check if metafield campaign has been deactivated
      if (!shouldRemove && gwpId.startsWith('metafield:')) {
        if (!activeMetafieldIds.has(gwpId)) {
          shouldRemove = true;
        }
      }

      if (shouldRemove) {
        console.log('[GWP] Removing expired/deactivated gift:', { variantId, gwpId });
        await removeGift(variantId, { silent: true });
      }
    }

    const subtotalExGifts = computeSubtotalExcludingGifts(cart);
    console.log('[GWP] Cart subtotal (excluding gifts):', subtotalExGifts, 'cents');

    const variantIdsInCart = new Set(
      items.map((it) => Number(it?.variant_id || it?.id || 0))
    );
    const silent = !prevCart; // treat the first pass (on load) as silent

    for (const gwp of state.gwps) {
      const presentLine = findGiftLineInCart(cart, gwp);
      const wasPresentLine = prevCart
        ? findGiftLineInCart(prevCart, gwp)
        : null;
      const qualifiesNow = qualifies(gwp, cart, subtotalExGifts);
      const userSuppressedKey = `gwp_removed_${gwp.variantId}`;
      let userSuppressed = localStorage.getItem(userSuppressedKey) === '1';

      console.log('[GWP] Checking GWP:', {
        gwp,
        subtotalExGifts,
        id: gwp.id,
        variantId: gwp.variantId,
        threshold: gwp.minThresholdCents,
        qualifiesNow,
        presentLine: !!presentLine,
        userSuppressed,
      });

      // Detect manual removal at any time: present before, absent now, still qualifies
      if (wasPresentLine && !presentLine && qualifiesNow) {
        localStorage.setItem(userSuppressedKey, '1');
        // Update local variable so we don't re-add it in this same loop iteration
        userSuppressed = true;
        console.log('[GWP] User manually removed gift, suppressing:', gwp.variantId);
      }

      if (qualifiesNow) {
        // If user suppressed this gift, skip re-adding ONCE (one-shot), then clear suppression
        if (userSuppressed) {
          console.log('[GWP] Skipping suppressed gift:', gwp.variantId);
          continue;
        }

        // If not present, add it (avoid duplicate add if same variant already present as non-gift)
        if (!presentLine) {
          console.log('[GWP] Adding gift:', gwp.variantId);
          await addGift(gwp.variantId, giftPropertiesFor(gwp), { silent });
        } else {
          console.log('[GWP] Gift already present:', gwp.variantId);
        }
      } else {
        // Not qualified, remove if present
        if (presentLine) {
          console.log('[GWP] Removing unqualified gift:', gwp.variantId);
          await removeGift(gwp.variantId, { silent });
        }
      }
    }

    // Reset suppression if cart is emptied
    if (Number(cart?.item_count || 0) === 0) {
      for (const gwp of state.gwps) {
        localStorage.removeItem(`gwp_removed_${gwp.variantId}`);
      }
    }
  }

  /**
   * Gift line properties for addToCart
   * @param {GwpConfigItem} gwp
   */
  function giftPropertiesFor(gwp) {
    /** @type {Record<string,string>} */
    const props = {
      _gift: 'true',
      _gwp_id: gwp.id,
    };
    // Hidden fields for display in custom gift line snippet
    if (gwp.title) props['_gwp_title'] = gwp.title;
    if (gwp.description) props['_gwp_description'] = gwp.description;
    // Store campaign end date for expiry detection
    if (gwp.endsAt) props['_gwp_ends_at'] = gwp.endsAt;
    return props;
  }

  /**
   * Render/Update threshold markers on the existing cart drawer progress bar
   * - Uses the existing <progress.free-shipping__progress-bar> element
   * - Adds an absolutely-positioned overlay with vertical markers for each GWP threshold
   * @param {any} cart
   */
  function renderProgress(cart) {
    const drawerBody = document.querySelector(
      '.drawer__body[data-cart-drawer-body]'
    );
    if (!drawerBody) return;

    const giftThresholds = state.gwps
      .filter(
        (g) =>
          g.criteria === 'minimum_purchase' &&
          typeof g.minThresholdCents === 'number'
      )
      .map((g) => Number(g.minThresholdCents));

    const shippingLimit = getShippingLimitCents();

    const markers = [];
    if (shippingLimit > 0)
      markers.push({ type: 'shipping', value: shippingLimit });
    for (const t of giftThresholds) markers.push({ type: 'gift', value: t });

    if (markers.length === 0) {
      drawerBody
        .querySelectorAll('.gwp-threshold-overlay')
        .forEach((el) => el.remove());
      return;
    }

    const allValues = markers.map((m) => m.value);
    const maxThreshold = Math.max(...allValues);
    const subtotalExGifts = computeSubtotalExcludingGifts(cart);
    const fillPct = clamp((subtotalExGifts / maxThreshold) * 100, 0, 100);

    // styles provided by assets/gift-with-purchase.css

    const bars = drawerBody.querySelectorAll(
      'progress.free-shipping__progress-bar[data-progress-bar]'
    );
    if (!bars?.length) return;

    bars.forEach((barEl) => {
      // Update progress value to reflect combined (max) threshold scale
      try {
        barEl.value = fillPct;
        barEl.setAttribute('value', String(fillPct));
      } catch {
        /* no-op */
      }

      // Remove existing overlay for a clean re-render
      const prevOverlay = barEl.parentElement?.querySelector(
        '.gwp-threshold-overlay'
      );
      if (prevOverlay) prevOverlay.remove();

      const parent = barEl.parentElement || drawerBody;
      const overlay = document.createElement('div');
      overlay.className = 'gwp-threshold-overlay';
      parent.appendChild(overlay);

      // Position overlay exactly over the progress bar
      const barRect = barEl.getBoundingClientRect();
      const parentRect = parent.getBoundingClientRect();
      overlay.style.left = `${barRect.left - parentRect.left}px`;
      overlay.style.top = `${barRect.top - parentRect.top}px`;
      overlay.style.width = `${barRect.width}px`;
      overlay.style.height = `${barRect.height}px`;

      // Exclude the last marker (end of bar) whether it's shipping or gift
      const overallMax = Math.max(
        shippingLimit || 0,
        giftThresholds.length ? Math.max(...giftThresholds) : 0
      );
      const markersToRender = markers.filter((m) => m.value !== overallMax);

      markersToRender.forEach((m) => {
        const leftPct = clamp((m.value / maxThreshold) * 100, 0, 100);
        const marker = document.createElement('div');
        marker.className = 'gwp-threshold-marker';
        marker.style.left = `${leftPct}%`;
        marker.title =
          m.type === 'shipping'
            ? `Free shipping: ${formatMoney(m.value)}`
            : `Gift threshold: ${formatMoney(m.value)}`;
        marker.setAttribute(
          'aria-label',
          m.type === 'shipping'
            ? `Free shipping threshold: ${formatMoney(m.value)}`
            : `Gift threshold: ${formatMoney(m.value)}`
        );
        if (m.type === 'shipping') marker.classList.add('is-shipping');
        if (subtotalExGifts >= m.value) marker.classList.add('is-reached');
        overlay.appendChild(marker);
      });
    });

    // Update CSS separator variable for themes that rely on it (min/max split)
    try {
      const all = markers.map((m) => m.value);
      const minVal = Math.min(...all);
      const percent = clamp((minVal / maxThreshold) * 100, 0, 100);
      document.documentElement.style.setProperty(
        '--gift-shipping-separator',
        `${percent}%`
      );
    } catch {
      /* no-op */
    }
  }

  function ensureProgressBarMarkerStyles() {
    /* styles moved to gift-with-purchase.css */
  }

  /**
   * Get free shipping threshold in cents from theme globals
   */
  function getShippingLimitCents() {
    try {
      const v = Number(window?.theme?.freeShipping?.limit || 0);
      return Number.isFinite(v) ? v : 0;
    } catch {
      return 0;
    }
  }

  /**
   * Money formatting using theme's format when available
   * Fallback to Intl.NumberFormat
   * @param {number} cents
   */
  function formatMoney(cents) {
    const amount = Number(cents || 0);
    const format =
      window?.theme?.moneyFormat ||
      '{{amount_no_decimals_with_comma_separator}}';
    try {
      return formatShopifyMoney(amount, format);
    } catch {
      try {
        const locale = document.documentElement.lang || 'en';
        // Shopify returns cents; divide to base currency
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency:
            window?.Shopify?.currency?.active ||
            window?.Shopify?.currency ||
            'USD',
        }).format(amount / 100);
      } catch {
        return (amount / 100).toFixed(2);
      }
    }
  }

  /**
   * Adopted Shopify-like formatting for money strings
   * (Adapted for robustness with most standard theme formats)
   * @param {number} cents
   * @param {string} format
   */
  function formatShopifyMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
      cents = parseInt(cents, 10);
    }
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholderRegex);
    let value = '';
    if (match) {
      switch (match[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'", '.');
          break;
        default:
          value = formatWithDelimiters(cents, 2);
      }
      return format.replace(placeholderRegex, value);
    }
    // Fallback
    return (cents / 100).toFixed(2);
  }

  /**
   * @param {number} cents
   * @param {number} precision
   * @param {string} thousands
   * @param {string} decimal
   */
  function formatWithDelimiters(
    cents,
    precision = 2,
    thousands = ',',
    decimal = '.'
  ) {
    if (isNaN(cents) || cents == null) return '0';
    const number = (cents / 100).toFixed(precision);
    const parts = number.split('.');
    const dollars = parts[0].replace(
      /(\d)(?=(\d{3})+(?!\d))/g,
      `$1${thousands}`
    );
    const centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  /**
   * Replace sections in DOM using bundled section HTML returned by Cart API.
   * This updates UI (cart icon bubble, live region, footer/items) quietly without opening the drawer.
   * @param {Record<string, string>} sectionsMap
   */
  function applyBundledSections(sectionsMap) {
    try {
      if (!sectionsMap || typeof sectionsMap !== 'object') return;
      Object.keys(sectionsMap).forEach((key) => {
        const html = sectionsMap[key];
        if (!html || typeof html !== 'string') return;
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const newRoot = tmp.firstElementChild;
        if (!newRoot || !newRoot.id) return;
        const existing = document.getElementById(newRoot.id);
        if (existing) {
          existing.replaceWith(newRoot);
        } else {
          // If not found by id, special-case known sections that have stable ids
          const fallbackId = `shopify-section-${key}`;
          const fallbackEl = document.getElementById(fallbackId);
          if (fallbackEl) {
            fallbackEl.replaceWith(newRoot);
          }
        }
      });

      // Announce update for any theme listeners
      try {
        document.dispatchEvent(
          new CustomEvent('theme:cart:sections:replaced', {
            detail: { sections: sectionsMap },
          })
        );
      } catch {
        /* no-op */
      }
    } catch (err) {
      /* no-op */
    }
  }

  /**
   * Quietly refresh the drawer items by replacing api-cart-items section
   * without dispatching theme events that might open the drawer.
   */
  async function refreshApiCartItemsSectionQuietly() {
    try {
      const cartUrl =
        window?.theme?.routes?.cart_url ||
        window?.Shopify?.routes?.cart_url ||
        `${Shopify.routes.root}cart`;
      const res = await fetch(`${cartUrl}?section_id=api-cart-items`, {
        credentials: 'same-origin',
      });
      if (!res.ok) return;
      const html = await res.text();
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const newSection = tmp.querySelector('#shopify-section-api-cart-items');
      const existing = document.getElementById(
        'shopify-section-api-cart-items'
      );
      if (newSection && existing) {
        existing.replaceWith(newSection);
      }
    } catch (e) {
      /* no-op */
    }
  }
  /**
   * Prevent drawer auto-open briefly by closing it if an open event is fired.
   */
  function preventDrawerOpenForMs(ms = 1500) {
    const onOpen = () => {
      try {
        document.dispatchEvent(
          new CustomEvent('theme:cart:close', { bubbles: true })
        );
      } catch {
        /* no-op */
      }
    };
    document.addEventListener('theme:cart:open', onOpen, { once: true });
    setTimeout(() => {
      document.removeEventListener('theme:cart:open', onOpen);
    }, ms);
  }
  function whenDomReady(cb) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', cb, { once: true });
    } else {
      cb();
    }
  }

  async function safeReadText(res) {
    try {
      return await res.text();
    } catch {
      return '';
    }
  }

  async function safeReadJson(res) {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }
})();
